/**
 * Utility functions for loading and parsing European energy price data
 */

import Papa from "papaparse";
import type {
	EuroPriceRecord,
	ParsedEuroPriceRecord,
	EuroLoadRecord,
	ParsedEuroLoadRecord,
} from "../types/euroData";

const DEFAULT_API_URL =
	"https://american-terawatt--hvdc-routing-fetch-euro.modal.run";
const DEFAULT_LOAD_API_URL =
	"https://american-terawatt--hvdc-routing-fetch-euro.modal.run/?data_type=load";

/**
 * Parse MTU (Market Time Unit) string to extract start and end timestamps
 * Example: "31/12/2022 23:00:00 - 01/01/2023 00:00:00"
 */
function parseMTU(mtu: string): { startTime: Date; endTime: Date } {
	const parts = mtu.split(" - ");
	if (parts.length !== 2) {
		throw new Error(`Invalid MTU format: ${mtu}`);
	}

	const [startStr, endStr] = parts;

	// Parse DD/MM/YYYY HH:mm:ss format
	const parseDateTime = (dateStr: string): Date => {
		const [datePart, timePart] = dateStr.split(" ");
		const [day, month, year] = datePart.split("/").map(Number);
		const [hour, minute, second] = timePart.split(":").map(Number);

		// Create UTC date
		return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
	};

	return {
		startTime: parseDateTime(startStr),
		endTime: parseDateTime(endStr),
	};
}

/**
 * Transform raw CSV record to parsed format
 */
function transformRecord(record: EuroPriceRecord): ParsedEuroPriceRecord {
	const { startTime, endTime } = parseMTU(record["MTU (UTC)"]);

	const intradayPrice = record["Intraday Price (EUR/MWh)"];
	const parsedIntradayPrice =
		typeof intradayPrice === "number"
			? intradayPrice
			: intradayPrice && intradayPrice !== ""
				? parseFloat(intradayPrice)
				: undefined;

	return {
		startTime,
		endTime,
		dayAheadPrice: record["Day-ahead Price (EUR/MWh)"],
		intradayPrice: parsedIntradayPrice,
		mtu: record["MTU (UTC)"],
	};
}

/**
 * Fetch and parse European energy price data from API
 */
export async function fetchEuroData(
	apiUrl: string = DEFAULT_API_URL,
): Promise<ParsedEuroPriceRecord[]> {
	try {
		const response = await fetch(apiUrl);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const csvText = await response.text();

		return new Promise((resolve, reject) => {
			Papa.parse<EuroPriceRecord>(csvText, {
				header: true,
				dynamicTyping: true,
				skipEmptyLines: true,
				complete: (results) => {
					try {
						const parsed = results.data.map(transformRecord);
						resolve(parsed);
					} catch (error) {
						reject(error);
					}
				},
				error: (error: Error) => {
					reject(error);
				},
			});
		});
	} catch (error) {
		console.error("Error fetching European data:", error);
		throw error;
	}
}

/**
 * Filter data by date range
 */
export function filterByDateRange(
	data: ParsedEuroPriceRecord[],
	startDate: Date,
	endDate: Date,
): ParsedEuroPriceRecord[] {
	return data.filter(
		(record) => record.startTime >= startDate && record.startTime <= endDate,
	);
}

/**
 * Get data for a specific date (all records for that day)
 */
export function getDataForDate(
	data: ParsedEuroPriceRecord[],
	date: Date,
): ParsedEuroPriceRecord[] {
	const startOfDay = new Date(date);
	startOfDay.setUTCHours(0, 0, 0, 0);

	const endOfDay = new Date(date);
	endOfDay.setUTCHours(23, 59, 59, 999);

	return filterByDateRange(data, startOfDay, endOfDay);
}

/**
 * Aggregate data to hourly intervals (if data is in smaller intervals)
 */
export function aggregateToHourly(
	data: ParsedEuroPriceRecord[],
): ParsedEuroPriceRecord[] {
	const hourlyMap = new Map<string, ParsedEuroPriceRecord[]>();

	// Group by hour
	data.forEach((record) => {
		const hourKey = new Date(record.startTime).setMinutes(0, 0, 0).toString();
		if (!hourlyMap.has(hourKey)) {
			hourlyMap.set(hourKey, []);
		}
		const hourRecords = hourlyMap.get(hourKey);
		if (hourRecords) {
			hourRecords.push(record);
		}
	});

	// Average prices for each hour
	return Array.from(hourlyMap.entries())
		.map(([hourKey, records]) => {
			const avgDayAheadPrice =
				records.reduce((sum, r) => sum + r.dayAheadPrice, 0) / records.length;

			const intradayPrices = records
				.map((r) => r.intradayPrice)
				.filter((p): p is number => p !== undefined);

			const avgIntradayPrice =
				intradayPrices.length > 0
					? intradayPrices.reduce((sum, p) => sum + p, 0) /
						intradayPrices.length
					: undefined;

			const firstRecord = records[0];
			const hourStart = new Date(parseInt(hourKey));
			const hourEnd = new Date(hourStart);
			hourEnd.setHours(hourEnd.getHours() + 1);

			return {
				startTime: hourStart,
				endTime: hourEnd,
				dayAheadPrice: avgDayAheadPrice,
				intradayPrice: avgIntradayPrice,
				mtu: firstRecord.mtu,
			};
		})
		.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

/**
 * Calculate daily average prices
 */
export function calculateDailyAverages(
	data: ParsedEuroPriceRecord[],
): Array<{ date: Date; avgPrice: number; count: number }> {
	const dailyMap = new Map<string, ParsedEuroPriceRecord[]>();

	// Group by day
	data.forEach((record) => {
		const dayKey = record.startTime.toISOString().split("T")[0];
		if (!dailyMap.has(dayKey)) {
			dailyMap.set(dayKey, []);
		}
		const dayRecords = dailyMap.get(dayKey);
		if (dayRecords) {
			dayRecords.push(record);
		}
	});

	// Calculate averages
	return Array.from(dailyMap.entries())
		.map(([dayKey, records]) => ({
			date: new Date(dayKey),
			avgPrice:
				records.reduce((sum, r) => sum + r.dayAheadPrice, 0) / records.length,
			count: records.length,
		}))
		.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Find peak and off-peak hours for a given day
 */
export function findPeakHours(dayData: ParsedEuroPriceRecord[]): {
	peakHour: ParsedEuroPriceRecord;
	offPeakHour: ParsedEuroPriceRecord;
} {
	if (dayData.length === 0) {
		throw new Error("No data provided");
	}

	const sorted = [...dayData].sort((a, b) => a.dayAheadPrice - b.dayAheadPrice);

	return {
		offPeakHour: sorted[0],
		peakHour: sorted[sorted.length - 1],
	};
}

/**
 * Parse MTU (Market Time Unit) string in CET/CEST to extract start and end timestamps
 * Example: "01/01/2025 00:00 - 01/01/2025 01:00"
 * Note: For now, we treat CET/CEST times as UTC for consistency with price data.
 * In a production system, we'd need proper timezone conversion based on DST rules.
 */
function parseMTUCET(mtu: string): { startTime: Date; endTime: Date } {
	const parts = mtu.split(" - ");
	if (parts.length !== 2) {
		throw new Error(`Invalid MTU format: ${mtu}`);
	}

	const [startStr, endStr] = parts;

	// Parse DD/MM/YYYY HH:mm format (note: no seconds in load data)
	const parseDateTime = (dateStr: string): Date => {
		const [datePart, timePart] = dateStr.trim().split(" ");
		const [day, month, year] = datePart.split("/").map(Number);
		const [hour, minute] = timePart.split(":").map(Number);

		// For now, treat CET/CEST as UTC for consistency with price data
		// This matches them on the same timeline even if there's a technical timezone difference
		return new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
	};

	return {
		startTime: parseDateTime(startStr),
		endTime: parseDateTime(endStr),
	};
}

/**
 * Transform raw CSV load record to parsed format
 */
function transformLoadRecord(record: EuroLoadRecord): ParsedEuroLoadRecord {
	const { startTime, endTime } = parseMTUCET(record["MTU (CET/CEST)"]);

	// Parse actual load (can be number, "N/A", or "-")
	const actualLoadRaw = record["Actual Total Load (MW)"];
	const actualLoad =
		typeof actualLoadRaw === "number"
			? actualLoadRaw
			: actualLoadRaw && actualLoadRaw !== "-" && actualLoadRaw !== "N/A"
				? parseFloat(actualLoadRaw)
				: undefined;

	// Parse forecast load (can be number, "N/A", or "-")
	const forecastLoadRaw = record["Day-ahead Total Load Forecast (MW)"];
	const forecastLoad =
		typeof forecastLoadRaw === "number"
			? forecastLoadRaw
			: forecastLoadRaw && forecastLoadRaw !== "-" && forecastLoadRaw !== "N/A"
				? parseFloat(forecastLoadRaw)
				: undefined;

	return {
		startTime,
		endTime,
		actualLoad: Number.isNaN(actualLoad as number) ? undefined : actualLoad,
		forecastLoad: Number.isNaN(forecastLoad as number)
			? undefined
			: forecastLoad,
		mtu: record["MTU (CET/CEST)"],
	};
}

/**
 * Fetch and parse European load data from API
 */
export async function fetchEuroLoadData(
	apiUrl: string = DEFAULT_LOAD_API_URL,
): Promise<ParsedEuroLoadRecord[]> {
	try {
		const response = await fetch(apiUrl);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const csvText = await response.text();

		return new Promise((resolve, reject) => {
			Papa.parse<EuroLoadRecord>(csvText, {
				header: true,
				dynamicTyping: true,
				skipEmptyLines: true,
				complete: (results) => {
					try {
						const parsed = results.data
							.map(transformLoadRecord)
							.filter((record) => record.actualLoad !== undefined); // Only keep records with actual load data
						resolve(parsed);
					} catch (error) {
						reject(error);
					}
				},
				error: (error: Error) => {
					reject(error);
				},
			});
		});
	} catch (error) {
		console.error("Error fetching European load data:", error);
		throw error;
	}
}
